// Formats an ISO timestamp (assumed UTC) as a wall-clock string in a target
// timezone. Used by the subscription cards so "Last run" reads in the same
// timezone as the "Schedule" line.
//
// Accepts both fixed UTC offsets ("UTC+10:00", "UTC-05:30", "UTC") and IANA
// timezone names ("Australia/Sydney"). Falls back to UTC if the value is
// missing or unparseable.

const parseOffsetMinutes = (tz) => {
	if (!tz || tz === "UTC") return 0;
	const match = String(tz).match(/^UTC([+-])(\d{2}):(\d{2})$/);
	if (!match) return null;
	const sign = match[1] === "-" ? -1 : 1;
	return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
};

// Backend timestamps without a "Z" or numeric offset are conventionally UTC
// but JavaScript will parse them as local browser time. Append Z so the
// instant is interpreted correctly.
const normaliseUtcIso = (iso) => {
	if (typeof iso !== "string") return iso;
	if (/[zZ]$/.test(iso) || /[+-]\d{2}:?\d{2}$/.test(iso)) return iso;
	return `${iso}Z`;
};

const formatWithIanaZone = (date, timezone) => {
	const datePart = date.toLocaleDateString("en-AU", { timeZone: timezone });
	const timePart = date.toLocaleTimeString("en-AU", {
		hour: "numeric",
		minute: "2-digit",
		timeZone: timezone,
	});
	return `${datePart}, ${timePart}`;
};

export const formatInOffset = (iso, timezone) => {
	if (!iso) return null;
	const date = new Date(normaliseUtcIso(iso));
	if (Number.isNaN(date.getTime())) return iso;

	const offsetMin = parseOffsetMinutes(timezone);
	if (offsetMin === null) {
		// Not a "UTC±HH:MM" string — try Intl with the value as an IANA name.
		try {
			return formatWithIanaZone(date, timezone);
		} catch {
			return formatWithIanaZone(date, "UTC");
		}
	}

	// Shift the underlying instant into the target offset, then format using
	// timeZone: UTC so the browser's own offset isn't applied a second time.
	const shifted = new Date(date.getTime() + offsetMin * 60_000);
	const datePart = shifted.toLocaleDateString("en-AU", { timeZone: "UTC" });
	const timePart = shifted.toLocaleTimeString("en-AU", {
		hour: "numeric",
		minute: "2-digit",
		timeZone: "UTC",
	});
	return `${datePart}, ${timePart}`;
};
